import {
  ForceDecision,
  OptimizelyDecisionWithOutUserContext,
} from '@awaze/mfe-common.lib.optimizely/dist/optimizely.types'
import { getCookie } from 'cookies-next'
import { OPTIMIZELY_FLAGS } from '../../constants'
import {
  getCurrentPublicRuntimeConfig,
  getUserDeviceType,
} from '../../utils/helpers/helpers'
import { useContext, useEffect, useState } from 'react'
import { GlobalProps } from '../../utils/globalProps'
import {
  OPTIMIZELY_COOKIE_NAME,
  Optimizely,
} from '@awaze/mfe-common.lib.optimizely'
import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import useOptimizelyExperimentsDatalayer from 'src/utils/dataLayer/useOptimizelyExperimentsDatalayer'
import { parseForceDecisionsFromUrlQuery } from '@awaze/mfe-common.lib.optimizely/dist/utils/parseForceDecisionsFromUrlQuery'

let decisions: Record<string, OptimizelyDecisionWithOutUserContext> | null =
  null

// This is a singleton promise to ensure we only create one instance of Optimizely at a time
let optimizelyInstPromise: Promise<Optimizely> | null = null
export function resetOptimizelyInstancePromise(): void {
  optimizelyInstPromise = null
}
const timeoutForOptimizelyClose = 5000 // 5 seconds

export const getSingleForcedDecision = (flag: string): ForceDecision[] => {
  if (!flag) return []

  const allDecisions = getForcedDecisions()

  const filteredDecisions = allDecisions.filter(
    (decision: ForceDecision) => decision.flagKey === flag,
  )

  return filteredDecisions
}

export const getForcedDecisions = (): ForceDecision[] => {
  if (typeof window === 'undefined' || !window.location) {
    return []
  }

  const params = new URLSearchParams(window.location.search)
  return parseForceDecisionsFromUrlQuery(Object.fromEntries(params))
}

const createUserAttributes = (
  brand: Brand,
  locale: string,
  host: string,
  isClientSide: boolean,
  awazeClient: string,
  userAgent?: string,
) => {
  const userDeviceType = getUserDeviceType()
  return {
    application: 'landing-pages-mfe',
    host,
    brand,
    locale,
    userDeviceType,
    isClientSide,
    ...(userAgent && { $opt_user_agent: userAgent, userAgent }),
    isGuestApp: awazeClient === 'guest-app',
  }
}

const getOptimizelyAdditionalConfig = () => {
  const { APP_ENV } = getCurrentPublicRuntimeConfig()
  return {
    logLevel:
      APP_ENV === 'prd' ||
      process.env.NEXT_PUBLIC_DISABLE_OPTIMIZELY_LOGGING === 'true'
        ? 'NEVER'
        : 'INFO',
    eventFlushInterval: 1000, // Flush events every second
  }
}

export const useOptimizelyUserDecisions = (
  useDecisions: boolean = true,
): Record<string, OptimizelyDecisionWithOutUserContext> | null => {
  const [optimizelyDecisions, setOptimizelyDecisions] = useState<Record<
    string,
    OptimizelyDecisionWithOutUserContext
  > | null>(null)
  const { optimizelySdkKey, brand, contentfulLocale, host, awazeClient } =
    useContext(GlobalProps)

  useEffect(() => {
    if (!optimizelySdkKey || !useDecisions || optimizelyDecisions) {
      return
    }
    getOptimizelyUserDecisionsForClient({
      optimizelySdkKey,
      brand,
      contentfulLocale,
      host,
      awazeClient,
    }).then((decisions) => {
      setOptimizelyDecisions(decisions)
    })
  }, [optimizelySdkKey, useDecisions])

  return optimizelyDecisions
}

const getOptimizelyUserDecisionsForClient = async ({
  optimizelySdkKey,
  brand,
  contentfulLocale,
  host,
  awazeClient,
}): Promise<Record<string, OptimizelyDecisionWithOutUserContext>> => {
  if (decisions) {
    return decisions
  }
  const userId = getCookie(OPTIMIZELY_COOKIE_NAME)
  if (!userId || userId === true) {
    return {}
  }

  let optimizelyInst: Optimizely
  try {
    if (!optimizelyInstPromise) {
      optimizelyInstPromise = Optimizely.getInstance({
        sdkKey: optimizelySdkKey,
        eventBatchSize: 10,
        additionalConfig: getOptimizelyAdditionalConfig(),
      })
    }
    optimizelyInst = await optimizelyInstPromise

    const userAttributes = createUserAttributes(
      brand,
      contentfulLocale,
      host,
      true,
      awazeClient,
    )

    const optimizelyDecisions =
      (await optimizelyInst?.getDecisions({
        experimentFlags: OPTIMIZELY_FLAGS,
        userId,
        userAttributes,
        forceDecisions: getForcedDecisions(),
      })) || {}
    decisions = optimizelyDecisions
    return optimizelyDecisions
  } catch (error) {
    window?.newrelic?.noticeError(
      `Error getting Optimizely decisions: ${error.message}`,
    )
    return {}
  } finally {
    setTimeout(() => {
      optimizelyInst?.close?.().catch((error) => {
        window?.newrelic?.noticeError(
          `Error closing Optimizely: ${error.message}`,
        )
      })
      optimizelyInstPromise = null // Reset the promise to allow a new instance to be created next time
    }, timeoutForOptimizelyClose)
  }
}

export const useSpecificOptimizelyUserDecision = (
  flag: string,
  useDecisions = true,
): OptimizelyDecisionWithOutUserContext | null => {
  const [optimizelyDecisions, setOptimizelyDecisions] = useState<Record<
    string,
    OptimizelyDecisionWithOutUserContext
  > | null>(null)
  const { optimizelySdkKey, brand, contentfulLocale, host, awazeClient } =
    useContext(GlobalProps)

  useEffect(() => {
    if (!optimizelySdkKey || !useDecisions || optimizelyDecisions) {
      return
    }

    getSpecificOptimizelyUserDecisionForClient({
      optimizelySdkKey,
      brand,
      contentfulLocale,
      host,
      flag,
      forceDecisions: getSingleForcedDecision(flag),
      awazeClient,
    }).then((decisions) => {
      setOptimizelyDecisions(decisions)
    })
  }, [optimizelySdkKey, useDecisions])

  const updateDatalayer = useOptimizelyExperimentsDatalayer(true)
  updateDatalayer(optimizelyDecisions?.[flag])

  return optimizelyDecisions?.[flag] || null
}

type SpecificOptimizelyUserDecisionForClientParams = {
  optimizelySdkKey: string
  brand: Brand
  contentfulLocale: string
  host: string
  awazeClient: string
  flag?: string
  forceDecisions?: ForceDecision[] | null
}

export const getSpecificOptimizelyUserDecisionForClient = async ({
  optimizelySdkKey,
  brand,
  contentfulLocale,
  host,
  flag,
  forceDecisions = null,
  awazeClient,
}: SpecificOptimizelyUserDecisionForClientParams): Promise<
  Record<string, OptimizelyDecisionWithOutUserContext>
> => {
  const userId = getCookie(OPTIMIZELY_COOKIE_NAME)
  if (!userId || userId === true) {
    return {}
  }

  if (!flag && !forceDecisions) {
    return {}
  }

  let optimizelyInstForSpecificDecision: Optimizely
  try {
    if (!optimizelyInstPromise) {
      optimizelyInstPromise = Optimizely.getInstance({
        sdkKey: optimizelySdkKey,
        eventBatchSize: 10,
        additionalConfig: getOptimizelyAdditionalConfig(),
      })
    }
    optimizelyInstForSpecificDecision = await optimizelyInstPromise

    const userAttributes = createUserAttributes(
      brand,
      contentfulLocale,
      host,
      true,
      awazeClient,
    )

    const optimizelyDecisions =
      (await optimizelyInstForSpecificDecision?.getDecisions({
        experimentFlags: flag ? [flag] : [],
        userId,
        userAttributes,
        ...(forceDecisions && { forceDecisions }),
      })) || {}
    decisions = optimizelyDecisions

    return optimizelyDecisions
  } catch (error) {
    window?.newrelic?.noticeError(
      `Error getting specific Optimizely decision: ${error.message}`,
    )
    return {}
  } finally {
    setTimeout(() => {
      optimizelyInstForSpecificDecision?.close?.().catch((error) => {
        window?.newrelic?.noticeError(
          `Error closing Optimizely: ${error.message}`,
        )
      })
      optimizelyInstPromise = null // Reset the promise to allow a new instance to be created next time
    }, timeoutForOptimizelyClose)
  }
}

type TrackEventProps = {
  optimizelySdkKey: string
  eventKey: string
  userId: string
  brand: Brand
  contentfulLocale: string
  host: string
  isClientSide: boolean
  awazeClient: string
  userAgent?: string
}

async function trackEvent({
  optimizelySdkKey,
  eventKey,
  userId,
  brand,
  contentfulLocale,
  host,
  isClientSide,
  awazeClient,
  userAgent,
}: TrackEventProps) {
  let optimizelyInst: Optimizely
  try {
    if (!optimizelyInstPromise) {
      optimizelyInstPromise = Optimizely.getInstance({
        sdkKey: optimizelySdkKey,
        eventBatchSize: 10,
        additionalConfig: getOptimizelyAdditionalConfig(),
      })
    }
    optimizelyInst = await optimizelyInstPromise

    const userAttributes = createUserAttributes(
      brand,
      contentfulLocale,
      host,
      isClientSide,
      awazeClient,
      userAgent,
    )
    await optimizelyInst.trackEvent({
      eventKey,
      userId,
      userAttributes,
    })
  } catch (error) {
    newrelic?.noticeError(
      `Error tracking optimizely ${eventKey} event: ${error.message}`,
    )
  } finally {
    setTimeout(() => {
      optimizelyInst?.close?.().catch((error) => {
        newrelic?.noticeError(`Error closing Optimizely: ${error.message}`)
      })
      optimizelyInstPromise = null // Reset the promise to allow a new instance to be created next time
    }, timeoutForOptimizelyClose)
  }
}

export const trackOptimizelyClientsideEvent = (
  optimizelySdkKey: string,
  eventKey: string,
  brand: Brand,
  contentfulLocale: string, // the contentful locale
  host: string,
  awazeClient: string,
  userAgent?: string,
): void => {
  const userId = getCookie(OPTIMIZELY_COOKIE_NAME)
  if (!userId || userId === true) {
    return
  }

  trackEvent({
    optimizelySdkKey,
    eventKey,
    userId,
    brand,
    contentfulLocale,
    host,
    isClientSide: true,
    awazeClient,
    userAgent,
  }).catch((error) => {
    newrelic?.noticeError(
      `Error tracking optimizely ${eventKey} event: ${error.message}`,
    )
  })
}
